// Scenario Management UI Components
// Shared React components for scenario management across all calculators

import React from 'react';
import { ASIC_DISCLAIMER } from './ScenarioService.js';

// Save Scenario Modal
export const SaveScenarioModal = ({ isOpen, onClose, onSave, scenarioName, setScenarioName }) => {
  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (scenarioName.trim()) {
      onSave(scenarioName.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Save Scenario</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scenario Name
            </label>
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter scenario name..."
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!scenarioName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Load Scenario Modal
export const LoadScenarioModal = ({ isOpen, onClose, scenarios, onLoad, onDelete }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-96 overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Load Scenario</h3>
        {scenarios.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No saved scenarios found</p>
        ) : (
          <div className="space-y-2">
            {scenarios.map((scenario) => (
              <div key={scenario.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{scenario.name}</div>
                  <div className="text-xs text-gray-500">
                    {scenario.createdAt ? new Date(scenario.createdAt).toLocaleDateString() : 'Unknown date'}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => onLoad(scenario)}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => onDelete(scenario.id)}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Export Modal
export const ExportModal = ({ isOpen, onClose, scenario, onExportJSON, onExportCSV, onExportPDF }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Export Scenario</h3>
        <p className="text-gray-600 mb-4">
          Export "{scenario?.name || 'Current Scenario'}" in your preferred format:
        </p>
        <div className="space-y-3">
          <button
            onClick={() => {
              onExportJSON();
              onClose();
            }}
            className="w-full px-4 py-3 text-left border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
          >
            <span className="font-medium">JSON</span>
            <span className="text-sm text-gray-500 ml-2">Complete scenario data</span>
          </button>
          <button
            onClick={() => {
              onExportCSV();
              onClose();
            }}
            className="w-full px-4 py-3 text-left border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
          >
            <span className="font-medium">CSV</span>
            <span className="text-sm text-gray-500 ml-2">Results data for spreadsheets</span>
          </button>
          <button
            onClick={() => {
              onExportPDF();
              onClose();
            }}
            className="w-full px-4 py-3 text-left border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
          >
            <span className="font-medium">PDF</span>
            <span className="text-sm text-gray-500 ml-2">Printable summary</span>
          </button>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Disclaimer Modal
export const DisclaimerModal = ({ isOpen, onAccept, onDecline }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Important Disclaimer</h3>
        <div className="text-sm text-gray-700 mb-6 whitespace-pre-line">
          {ASIC_DISCLAIMER}
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onDecline}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Decline
          </button>
          <button
            onClick={onAccept}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            I Understand and Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// Scenario Toolbar
export const ScenarioToolbar = ({ 
  onSave, 
  onLoad, 
  onExport, 
  onShowDisclaimer,
  hasResults = false,
  calculatorType = 'basic',
  canManageScenarios = true
}) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onSave}
            disabled={!hasResults || !canManageScenarios}
            className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            💾 Save Scenario
          </button>
          <button
            onClick={onLoad}
            disabled={!canManageScenarios}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📁 Load Scenario
          </button>
          <button
            onClick={onExport}
            disabled={!hasResults || !canManageScenarios}
            className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📊 Export Results
          </button>
        </div>
        <button
          onClick={onShowDisclaimer}
          className="px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          ⚠️ Disclaimer
        </button>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        {calculatorType.charAt(0).toUpperCase() + calculatorType.slice(1)} Calculator Scenarios
        {!canManageScenarios && (
          <span className="ml-2 text-amber-700 font-medium">- Save/Load/Export available for subscribers</span>
        )}
      </div>
    </div>
  );
};
